import { useState, useEffect } from 'react';
import { WidgetConfig } from '@/components/dashboard/DraggableWidget';
import { useUserSettings } from './useUserSettings';

const CAISSE_WIDGETS: WidgetConfig[] = [
  { id: 'products-kpi', type: 'kpi', title: 'Produits', size: 'small', config: { kpiType: 'total-products' } },
  { id: 'value-kpi', type: 'kpi', title: 'Valeur stock', size: 'small', config: { kpiType: 'stock-value' } },
  { id: 'low-stock-kpi', type: 'kpi', title: 'Stocks faibles', size: 'small', config: { kpiType: 'low-stock' } },
  { id: 'sales-kpi', type: 'kpi', title: 'Ventes', size: 'small', config: { kpiType: 'sales-count' } },
  { id: 'stock-level-chart', type: 'chart', title: 'Niveaux de stock', size: 'full', config: { chartType: 'stock-levels' } },
  { id: 'sales-chart', type: 'chart', title: 'Graphique des ventes', size: 'medium', config: { chartType: 'sales' } },
  { id: 'payment-methods-chart', type: 'chart', title: 'Top produits', size: 'medium', config: { chartType: 'payment-methods' } },
  { id: 'product-table', type: 'table', title: 'Gestion des produits', size: 'full', config: { tableType: 'products' } },
];

const RETRAIT_WIDGETS: WidgetConfig[] = [
  { id: 'products-kpi', type: 'kpi', title: 'Produits', size: 'small', config: { kpiType: 'total-products' } },
  { id: 'value-kpi', type: 'kpi', title: 'Valeur stock', size: 'small', config: { kpiType: 'stock-value' } },
  { id: 'withdrawals-kpi', type: 'kpi', title: 'Retraits', size: 'small', config: { kpiType: 'sales-count' } },
  { id: 'low-stock-kpi', type: 'kpi', title: 'Stocks faibles', size: 'small', config: { kpiType: 'low-stock' } },
  { id: 'inventory-chart', type: 'chart', title: 'Inventaire', size: 'full', config: { chartType: 'inventory' } },
  { id: 'withdrawals-chart', type: 'chart', title: 'Graphique des retraits', size: 'medium', config: { chartType: 'withdrawals-evolution', displayType: 'line' } },
  { id: 'withdrawals-pie-chart', type: 'chart', title: 'Répartition retraits', size: 'medium', config: { chartType: 'withdrawals-pie' } },
  { id: 'product-table', type: 'table', title: 'Gestion des produits', size: 'full', config: { tableType: 'products' } },
];

const ANALYTICS_WIDGETS: WidgetConfig[] = [
  { id: 'revenue-kpi', type: 'kpi', title: 'Chiffre d\'affaires', size: 'small', config: { kpiType: 'revenue' } },
  { id: 'products-sold-kpi', type: 'kpi', title: 'Produits vendus', size: 'small', config: { kpiType: 'products-sold' } },
  { id: 'average-basket-kpi', type: 'kpi', title: 'Panier moyen', size: 'small', config: { kpiType: 'average-basket' } },
  { id: 'sales-chart', type: 'chart', title: 'Graphique des ventes', size: 'medium', config: { chartType: 'sales-chart' } },
  { id: 'sales-pie-chart', type: 'chart', title: 'Répartition des ventes (camembert)', size: 'medium', config: { chartType: 'sales-pie' } },
  { id: 'best-sellers-table', type: 'table', title: 'Meilleures ventes', size: 'full', config: { tableType: 'best-sellers' } },
];

const STATISTICS_WIDGETS: WidgetConfig[] = [
  { id: 'welcome-stats', type: 'custom-table', title: 'Bienvenue', size: 'full', config: { 
    customData: {
      name: 'Exemple de tableau',
      columns: [
        { id: '1', name: 'Produit', type: 'text' },
        { id: '2', name: 'Quantité', type: 'number' },
        { id: '3', name: 'Prix', type: 'number' }
      ],
      rows: [
        { '1': 'Produit A', '2': '10', '3': '25.50' },
        { '1': 'Produit B', '2': '5', '3': '15.00' },
        { '1': 'Produit C', '2': '8', '3': '30.25' }
      ]
    }
  } },
];

const RETRAIT_ANALYTICS_WIDGETS: WidgetConfig[] = [
  { id: 'total-withdrawals-kpi', type: 'kpi', title: 'Total retraits', size: 'small', config: { kpiType: 'total-withdrawals' } },
  { id: 'items-withdrawn-kpi', type: 'kpi', title: 'Articles retirés', size: 'small', config: { kpiType: 'items-withdrawn' } },
  { id: 'products-concerned-kpi', type: 'kpi', title: 'Produits concernés', size: 'small', config: { kpiType: 'products-concerned' } },
  { id: 'average-per-product-kpi', type: 'kpi', title: 'Moyenne par produit', size: 'small', config: { kpiType: 'average-per-product' } },
  { id: 'withdrawals-pie-chart', type: 'chart', title: 'Répartition des produits', size: 'medium', config: { chartType: 'withdrawals-pie' } },
  { id: 'withdrawals-chart', type: 'chart', title: 'Évolution des retraits', size: 'medium', config: { chartType: 'withdrawals-chart' } },
  { id: 'top-withdrawn-products-table', type: 'table', title: 'Produits les plus retirés', size: 'full', config: { tableType: 'top-withdrawn-products' } },
];

const DEFAULT_WIDGETS = CAISSE_WIDGETS;

export const useDashboardLayout = (mode?: string) => {
  const { getDashboardLayout, saveDashboardLayout, isLoading: settingsLoading } = useUserSettings();

  const getDefaultWidgets = (currentMode?: string) => {
    switch (currentMode) {
      case 'caisse':
        return CAISSE_WIDGETS;
      case 'retrait':
        return RETRAIT_WIDGETS;
      case 'analytics':
        return ANALYTICS_WIDGETS;
      case 'retrait-analytics':
        return RETRAIT_ANALYTICS_WIDGETS;
      case 'statistics':
        return STATISTICS_WIDGETS;
      default:
        return DEFAULT_WIDGETS;
    }
  };

  const [widgets, setWidgets] = useState<WidgetConfig[]>(getDefaultWidgets(mode));
  const [originalWidgets, setOriginalWidgets] = useState<WidgetConfig[]>(getDefaultWidgets(mode));
  const [isEditMode, setIsEditMode] = useState(false);

  // Load layout from Supabase based on mode
  useEffect(() => {
    if (settingsLoading) return;
    
    const modeKey = mode || 'default';
    const savedLayout = getDashboardLayout(modeKey);
    const defaultWidgets = getDefaultWidgets(mode);
    
    if (savedLayout && Array.isArray(savedLayout) && savedLayout.length > 0) {
      setWidgets(savedLayout);
      setOriginalWidgets(savedLayout);
    } else {
      // Fallback to localStorage for migration
      const storageKey = mode ? `dashboard-layout-${mode}` : 'dashboard-layout';
      const localSavedLayout = localStorage.getItem(storageKey);
      
      if (localSavedLayout) {
        try {
          const parsedLayout = JSON.parse(localSavedLayout);
          setWidgets(parsedLayout);
          setOriginalWidgets(parsedLayout);
          // Migrate to database and clear localStorage
          saveDashboardLayout(modeKey, parsedLayout);
          localStorage.removeItem(storageKey);
        } catch (error) {
          console.error('Failed to migrate dashboard layout:', error);
          setWidgets(defaultWidgets);
          setOriginalWidgets(defaultWidgets);
        }
      } else {
        setWidgets(defaultWidgets);
        setOriginalWidgets(defaultWidgets);
      }
    }
  }, [mode, settingsLoading, getDashboardLayout, saveDashboardLayout]);

  // Save layout to Supabase based on mode
  const saveLayout = () => {
    const modeKey = mode || 'default';
    saveDashboardLayout(modeKey, widgets);
    setIsEditMode(false);
  };

  // Auto-save layout when not in edit mode
  const autoSave = (newWidgets: WidgetConfig[]) => {
    if (!isEditMode) {
      const modeKey = mode || 'default';
      saveDashboardLayout(modeKey, newWidgets);
    }
  };

  const reorderWidgets = (newWidgets: WidgetConfig[]) => {
    setWidgets(newWidgets);
    autoSave(newWidgets);
  };

  const updateWidget = (updatedWidget: WidgetConfig) => {
    const newWidgets = widgets.map(widget => 
      widget.id === updatedWidget.id ? updatedWidget : widget
    );
    setWidgets(newWidgets);
    autoSave(newWidgets);
  };

  const removeWidget = (widgetId: string) => {
    const newWidgets = widgets.filter(widget => widget.id !== widgetId);
    setWidgets(newWidgets);
    autoSave(newWidgets);
  };

  const addWidget = (type: string, config: any) => {
    const newWidget: WidgetConfig = {
      id: `${type}-${Date.now()}`,
      type: type as any,
      title: config.title || 'Nouveau widget',
      size: config.size || (type === 'kpi' ? 'small' : 'medium'),
      config
    };
    const newWidgets = [...widgets, newWidget];
    setWidgets(newWidgets);
    autoSave(newWidgets);
  };

  const addWidgetAt = (index: number, type: string, config: any) => {
    const newWidget: WidgetConfig = {
      id: `${type}-${Date.now()}`,
      type: type as any,
      title: config.title || 'Nouveau widget',
      size: config.size || (type === 'kpi' ? 'small' : 'medium'),
      config
    };
    
    const newWidgets = [...widgets];
    newWidgets.splice(index, 0, newWidget);
    setWidgets(newWidgets);
    autoSave(newWidgets);
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      saveLayout();
    } else {
      setOriginalWidgets([...widgets]);
      setIsEditMode(true);
    }
  };

  const cancelEdit = () => {
    setWidgets([...originalWidgets]);
    setIsEditMode(false);
  };

  const resetToDefault = () => {
    const defaultWidgets = getDefaultWidgets(mode);
    setWidgets(defaultWidgets);
    const modeKey = mode || 'default';
    saveDashboardLayout(modeKey, defaultWidgets);
  };

  return {
    widgets,
    isEditMode,
    reorderWidgets,
    updateWidget,
    removeWidget,
    addWidget,
    addWidgetAt,
    toggleEditMode,
    cancelEdit,
    resetToDefault
  };
};